/**
 * Which release this tab runs, and which one is deployed (ADR 0117).
 *
 * A release's web image bakes its tag into the bundle and writes the same tag
 * to /version.json, which nginx never lets be cached. A tab opened before a
 * deploy keeps running the old bundle, so it reads /version.json when the
 * window gets focus and with each of the day view's polls, and says so when
 * the two differ (`NewVersionBanner`).
 */
import type { QueryClient } from "@tanstack/react-query";

export const VERSION_URL = "/version.json";

export const VERSION_QUERY_KEY = ["version"] as const;

/** A dev build, or one built without a version, has none: then nothing is checked. */
export function bundleVersion(dev: boolean, baked: string): string | null {
  return dev || baked === "" ? null : baked;
}

/** This tab's own release. */
export const BUNDLE_VERSION = bundleVersion(import.meta.env.DEV, __APP_VERSION__);

/**
 * The deployed release, from /version.json. Rejects when it can't be read:
 * no answer, no file (a build without a version), or no version in it.
 */
export async function fetchDeployedVersion(fetchFn: typeof fetch): Promise<string> {
  const response = await fetchFn(VERSION_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`${VERSION_URL}: HTTP ${String(response.status)}`);
  }
  const body: unknown = await response.json();
  if (typeof body === "object" && body !== null && "version" in body) {
    const { version } = body;
    if (typeof version === "string" && version !== "") {
      return version;
    }
  }
  throw new Error(`${VERSION_URL} has no version`);
}

/**
 * Reads /version.json again, if this tab checks at all (a dev build doesn't).
 * A check already under way is reused, not repeated.
 */
export function checkForNewVersion(queryClient: QueryClient): void {
  void queryClient.refetchQueries({ queryKey: VERSION_QUERY_KEY, type: "active" }, { cancelRefetch: false });
}
